import { useState, type ReactNode, type ThHTMLAttributes } from 'react';
import { Select } from '../ui/Select';
import { ct } from './bind';

// Client-side sort for the console's plain list tables (complete lists the page already holds; a server-paged list
// sorts on the server, like DataGrid). The sort is `key` / `-key` / '' (the list's own order), so it fits a URL
// query parameter; the header toggles ascending, descending, back to the list's order.

export interface SortKey<T> {
  label: string;
  value: (row: T) => unknown;
  /** The first click sorts descending (dates, counts, sizes: newest or largest first). */
  desc?: boolean;
}

export type SortKeys<T> = Record<string, SortKey<T>>;

export interface Sort<T> {
  /** The rows in the chosen order. */
  rows: T[];
  /** `key`, `-key` or '' for the list's own order. */
  value: string;
  set: (value: string) => void;
  /** Next state of a header click on `key`. */
  toggle: (key: string) => void;
  keys: SortKeys<T>;
  /** Sorts another list the same way (rows within groups). */
  sortRows: <R extends T>(rows: readonly R[]) => R[];
}

/** Empty values last in both directions; numbers and dates by value, text with numeric collation, ignoring case. */
export function compareSortValues(a: unknown, b: unknown, desc = false): number {
  const empty = (v: unknown) => v == null || v === '' || (typeof v === 'number' && Number.isNaN(v));
  if (empty(a) || empty(b)) return empty(a) === empty(b) ? 0 : empty(a) ? 1 : -1;
  const x = a instanceof Date ? a.getTime() : typeof a === 'boolean' ? Number(a) : a;
  const y = b instanceof Date ? b.getTime() : typeof b === 'boolean' ? Number(b) : b;
  const c =
    typeof x === 'number' && typeof y === 'number'
      ? x - y
      : String(x).localeCompare(String(y), undefined, { numeric: true, sensitivity: 'base' });
  return desc ? -c : c;
}

/**
 * Sorts `rows` by one of `keys`. `state` is where the sort lives, e.g. `useParam('sort', '')` so the order is part
 * of a shared link; without it the page keeps it. An unknown key (an old link) leaves the list's order.
 */
export function useSort<T>(
  rows: readonly T[] | null | undefined,
  keys: SortKeys<T>,
  state?: [string, (value: string) => void]
): Sort<T> {
  const own = useState('');
  const [value, set] = state ?? own;
  const desc = value.startsWith('-');
  const key = keys[desc ? value.slice(1) : value];
  const sortRows = <R extends T>(list: readonly R[]): R[] =>
    key ? [...list].sort((a, b) => compareSortValues(key.value(a), key.value(b), desc)) : [...list];
  const toggle = (k: string) => {
    const first = keys[k]?.desc ? `-${k}` : k;
    const second = keys[k]?.desc ? k : `-${k}`;
    set(value === first ? second : value === second ? '' : first);
  };
  return { rows: sortRows(rows ?? []), value, set, toggle, keys, sortRows };
}

/** A header cell that sorts by `by` (a button inside, `aria-sort` on the cell); the text defaults to the key's label. */
export function SortTh<T>({
  sort,
  by,
  children,
  className,
  ...rest
}: { sort: Sort<T>; by: string; children?: ReactNode } & ThHTMLAttributes<HTMLTableCellElement>) {
  const dir = sort.value === by ? 'ascending' : sort.value === `-${by}` ? 'descending' : 'none';
  return (
    <th
      scope="col"
      aria-sort={dir}
      className={[className, dir !== 'none' && 'nyk-ui-sorted', dir === 'descending' && 'nyk-ui-desc']
        .filter(Boolean)
        .join(' ')}
      {...rest}
    >
      <button type="button" className="nyk-ui-sort" onClick={() => sort.toggle(by)}>
        {children ?? sort.keys[by]?.label}
      </button>
    </th>
  );
}

/** The sort as a select, for phones where a card list has no header (hidden from 721 px up). */
export function SortSelect<T>({ sort }: { sort: Sort<T> }) {
  const options = [
    { value: '', label: ct('sort.default') },
    ...Object.entries(sort.keys).flatMap(([k, s]) => {
      const asc = { value: k, label: ct('sort.ascending', { label: s.label }) };
      const desc = { value: `-${k}`, label: ct('sort.descending', { label: s.label }) };
      return s.desc ? [desc, asc] : [asc, desc];
    }),
  ];
  return (
    <span className="nyk-sort-select">
      <Select
        aria-label={ct('sort.by')}
        value={sort.value}
        onChange={sort.set}
        options={options}
        searchable={false}
      />
    </span>
  );
}
